import { useRef, useCallback, useMemo } from 'react'
import { FormControlLabel, MenuItem, Radio, Typography } from '@mui/material'

import { naturalSort } from '@/server-data'
import {
  ListHeaderFilterMenu,
  ListHeaderFilterInput,
  ListHeaderFilterContainer,
  type ListHeaderSecondaryActionRef,
} from '@/components/List'

import { useTexts } from '../../useTexts'

import { allUserTypes } from './hooks/useFilterList'
import { useFilter } from './hooks/useFilter'

export function Filter() {
  const { query, filter, setFilter, setQuery, editing, noEntries } = useFilter()

  const texts = useTexts()

  const filterMenuRef = useRef<ListHeaderSecondaryActionRef>(null)
  const filterActive = useMemo(() => filter.type !== 'all', [filter])

  const onClearAllClick = useCallback(() => {
    setFilter({ type: 'all' })
    filterMenuRef.current?.close()
  }, [setFilter])

  const filterOptions = useMemo(
    () =>
      allUserTypes
        .map(id => ({ id, label: texts.type(id) }))
        .sort((a, b) => naturalSort(a.label, b.label)),
    [texts],
  )

  const disabled = editing || noEntries

  return (
    <ListHeaderFilterContainer>
      <ListHeaderFilterInput
        placeholder={texts.filterPlaceholder}
        disabled={disabled}
        setQuery={setQuery}
        testId="list-header-filter-input"
        query={query}
      />

      <ListHeaderFilterMenu filterActive={filterActive} ref={filterMenuRef} disabled={disabled}>
        <MenuItem
          data-testid="list-header-filter-filterOption-type-all"
          data-trackid="list-header-filter-filterOption-type-all"
          onClick={onClearAllClick}
        >
          <FormControlLabel
            label={texts.any}
            control={
              <Radio
                size="small"
                value="all"
                checked={filter.type === 'all'}
                readOnly
                disableRipple
              />
            }
          />
        </MenuItem>

        {filterOptions.map(({ id, label }) => (
          <MenuItem
            key={id}
            onClick={() => setFilter({ type: id })}
            data-testid={`list-header-filter-filterOption-eventId-${id}`}
            data-trackid={`list-header-filter-filterOption-eventId-${id}`}
          >
            <FormControlLabel
              label={label}
              control={
                <Radio
                  size="small"
                  value={id}
                  checked={filter.type !== 'all' && filter.type === id}
                  readOnly
                  disableRipple
                />
              }
            />
          </MenuItem>
        ))}

        {filterActive && (
          <MenuItem
            onClick={onClearAllClick}
            data-testid="list-header-filter-filterOption-clear"
            data-trackid="list-header-filter-filterOption-clear"
          >
            <Typography fontSize={15} color="primary">
              {texts.clearAll}
            </Typography>
          </MenuItem>
        )}
      </ListHeaderFilterMenu>
    </ListHeaderFilterContainer>
  )
}
